import "./Users.scss";
import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { HomeColumns } from "../../SourceData";
import { DatePicker } from "antd";
import { message } from "antd";
import UserPassword from "./../../assets/user-password.svg";
import ShowUser from "./../../assets/show-user.svg";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import SearchIcon from "@mui/icons-material/Search";
import MessageModals from "../../components/MessageModals/MessageModals";
import api from "../../utils/api";
import usePermissions from "../../hooks/usePermissions";
import SwipeableModals from "../Home/SwipeableModals/SwipeableModals";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
const { RangePicker } = DatePicker;

const Users = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [registerType, setRegisterType] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [view, setView] = useState("admins");
  const [currentModalView, setCurrentModalView] = useState("changePassword");
  const [openDelete, setOpenDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [page, setPage] = useState(0); // DataGrid uses 0-based pagination
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const { hasPermission } = usePermissions();

  const getData = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page + 1, // Convert to 1-based for API
        limit: pageSize, // Use limit instead of per_page to match API response
      });

      if (query) {
        params.append("search", query);
      }

      if (registerType) {
        params.append("register_type", registerType);
      }

      if (dateRange) {
        if (dateRange[0]) {
          params.append("date_from", dateRange[0].format("YYYY-MM-DD"));
        }
        if (dateRange[1]) {
          params.append("date_to", dateRange[1].format("YYYY-MM-DD"));
        }
      }

      const res = await api.get(`/users?${params.toString()}`);

      // Handle the API response structure properly
      if (res.data.status === "success") {
        setUsers(res.data.data || []);
        setTotalRows(res.data.total || 0); // Use total from API response
      } else {
        // Handle error case
        setUsers([]);
        setTotalRows(0);
        message.error("حدث خطأ في تحميل البيانات");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setTotalRows(0);
      message.error("حدث خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [query, registerType, dateRange, page, pageSize]);

  const handleActive = async (id) => {
    setLoading(true);
    try {
      await api.put(`/users/${id}/activateToggle`);
      getData();
      message.success("تم تعديل حالة المستخدم بنجاح");
    } catch (error) {
      message.error("حدث خطأ في تعديل حالة المستخدم");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (view) => {
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  const handleRegisterTypeChange = (event) => {
    setRegisterType(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle pagination changes
  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  // Handle search with debouncing (optional improvement)
  const handleSearchChange = (event) => {
    setQuery(event.target.value);
    setPage(0); // Reset to first page when search changes
  };

  const actionColumn = [
    hasPermission(23) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.active;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row.id)}
          />
        );
      },
    },

    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,

      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(28) && (
              <div
                className="cell-btn"
                onClick={() => {
                  setUser(params.row);
                  handleOpen("changePassword");
                }}
              >
                <img src={UserPassword} alt="" />
              </div>
            )}

            {hasPermission(27) && (
              <Link className="cell-btn" to={`/users/${params.row.id}`}>
                <img src={ShowUser} alt="" />
              </Link>
            )}
            {hasPermission(25) && (
              <div
                className="cell-btn"
                onClick={() => {
                  setUser(params.row);
                  handleOpen("editUser");
                }}
              >
                <img src={EditUser} alt="" />
              </div>
            )}
            {hasPermission(26) && (
              <div
                className="cell-btn"
                onClick={() => {
                  setOpenDelete(true);
                  setUser(params.row);
                }}
              >
                <img src={DeleteUser} alt="" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">المستخدمين</p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder={
                view === "admins"
                  ? "ابحث بالاسم او البريد الالكتروني او رقم الموبايل"
                  : "بحث..."
              }
              onChange={handleSearchChange}
              sx={{
                width: isMobile
                  ? "100%"
                  : isTablet || view === "permissions"
                  ? "300px"
                  : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              sx={{
                width: isMobile
                  ? "100%"
                  : isTablet || view === "permissions"
                  ? "300px"
                  : "360px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                طريقة التسجيل
              </InputLabel>
              <Select
                disableScrollLock
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                displayEmpty={true}
                sx={{ borderRadius: "8px" }}
                value={registerType}
                onChange={handleRegisterTypeChange}
              >
                <MenuItem value="">الجميع</MenuItem>
                <MenuItem value={1}>رقم الهاتف</MenuItem>
                <MenuItem value={4}>فيس بوك</MenuItem>
                <MenuItem value={2}>جيميل</MenuItem>
                <MenuItem value={3}>ابل اي دي</MenuItem>
              </Select>
            </FormControl>

            <RangePicker
              format={"MMM D"}
              style={{
                width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                padding: "0px 10px",
                direction: "ltr",
              }}
              placeholder={["من", "الي"]}
              className="range-picker"
              onChange={handleDateRangeChange}
              value={dateRange}
            />
          </div>

          {/* <BlueButton
            text="اضافة مستخدم"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant="fill"
            onClick={() => handleOpen("createUser")}
            icon={<img src={AddGif} alt="" />}
          /> */}
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={HomeColumns.concat(actionColumn)}
          rows={users.map((row, index) => ({
            ...row,
            index: page * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          paginationMode="server"
          rowCount={totalRows}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{
            page,
            pageSize,
          }}
          onPaginationModelChange={handlePaginationModelChange}
        />
      </div>

      <MessageModals
        open={openDelete}
        currentView="delete"
        route={`/users/${user.id}`}
        onClose={() => {
          setOpenDelete(false);
        }}
        onDataChange={getData}
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        currentView={currentModalView}
        user={user}
        onDataChange={getData}
      />
    </div>
  );
};

export default Users;
